import AuthPromptDialog from "@/components/Auth/AuthPromptDialog";
import { useAuth } from "@/hooks/useAuth";
import {
  canLogConditionFeedback,
  logConditionFeedback,
  type ConditionFeedbackReason,
} from "@/lib/spot-observations";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import { Box, Chip, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import { useMemo, useState } from "react";

const REASONS: { value: ConditionFeedbackReason; label: string }[] = [
  { value: "too_big", label: "Too big" },
  { value: "too_small", label: "Too small" },
  { value: "cleaner", label: "Cleaner" },
  { value: "messier", label: "Messier" },
  { value: "wind_worse", label: "Wind worse" },
  { value: "tide_off", label: "Tide off" },
];

interface ConditionFeedbackProps {
  regionId: string;
  breakId: string;
}

export default function ConditionFeedback({ regionId, breakId }: ConditionFeedbackProps) {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [showReasons, setShowReasons] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [didSubmit, setDidSubmit] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const canSubmit = useMemo(
    () => !didSubmit && canLogConditionFeedback(regionId, breakId),
    [breakId, didSubmit, regionId],
  );

  const submit = async (
    forecastMatched: boolean,
    feedbackReason?: ConditionFeedbackReason,
  ) => {
    if (!user) {
      setAuthOpen(true);
      return;
    }

    if (!canLogConditionFeedback(regionId, breakId)) {
      setStatus("Feedback already saved for this spot.");
      setDidSubmit(true);
      setShowReasons(false);
      return;
    }

    setSubmitting(true);
    setStatus(null);
    try {
      await logConditionFeedback({
        regionId,
        breakId,
        user,
        forecastMatched,
        feedbackReason,
      });
      setStatus(forecastMatched ? "Thanks. Conditions confirmed." : "Got it. Thanks.");
      setDidSubmit(true);
      setShowReasons(false);
    } catch {
      setStatus("Could not save feedback. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          color: "ink.muted",
        }}
      >
        <Typography component="span" sx={{ fontSize: "0.75rem", color: "ink.muted" }}>
          Is this accurate?
        </Typography>
        <Tooltip title="Looks right">
          <span>
            <IconButton
              size="small"
              aria-label="Looks right"
              disabled={submitting || !canSubmit}
              onClick={() => submit(true)}
              sx={{
                width: 30,
                height: 30,
                color: "ink.muted",
                "&:hover": { color: "kelp.main", bgcolor: "transparent" },
              }}
            >
              <ThumbUpOffAltIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Looks off">
          <span>
            <IconButton
              size="small"
              aria-label="Looks off"
              disabled={submitting || !canSubmit}
              onClick={() => {
                if (!user) {
                  setAuthOpen(true);
                  return;
                }
                setShowReasons((open) => !open);
                setStatus(null);
              }}
              sx={{
                width: 30,
                height: 30,
                color: "ink.muted",
                "&:hover": { color: "buoy.main", bgcolor: "transparent" },
              }}
            >
              <ThumbDownOffAltIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Collapse in={showReasons} timeout={180}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
          {REASONS.map((reason) => (
            <Chip
              key={reason.value}
              label={reason.label}
              size="small"
              variant="outlined"
              disabled={submitting}
              onClick={() => submit(false, reason.value)}
              sx={{
                height: 26,
                borderRadius: 1,
                borderColor: "rule.strong",
                color: "ink.main",
                fontSize: "0.75rem",
                "&:hover": {
                  borderColor: "ink.main",
                  bgcolor: "transparent",
                },
              }}
            />
          ))}
        </Box>
      </Collapse>

      {status && (
        <Typography sx={{ mt: 0.75, fontSize: "0.75rem", color: "ink.muted" }}>
          {status}
        </Typography>
      )}

      <AuthPromptDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        source="condition_feedback"
        reason="Sign in to confirm whether the current surf conditions look right."
      />
    </Box>
  );
}
